import { Dispatch } from 'redux';
import gql, { serializeGQLObject } from '../../services/gql';
import { State } from '../reducers';
import { CompareResultType } from '../../organisms/ComparePlayersTable';
import {
  setUserSettingsLoadingAction,
  setUserSettingsLoadedAction,
  setSearchColumnsAction,
  setSavedPlayersColumnsAction,
  setTeamPlayersColumnsAction,
  setComparePlayersColumnsAction,
  setManageUsersColumnsAction,
  setManageProgramsColumnsAction,
  setComparePlayersResultTypeAction,
  setIsBoardLastViewOnDashboardAction,
  setUserSettingCtbnUsername,
  setCollegeDashboardSelectedYearAction,
  setNflDashboardSelectedYearAction,
  setProfileStatsCategoriesAction,
  setProfileStatsCategoriesNFLAction,
  setPlayerEvaluationValidationIsAllAction,
  setPlayerValidationSettingAction,
} from '../actions/user-settings';
import UserSettings from '../../types/UserSettings';
import Column from '../../types/Column';

export function fetchUserSettings (isRefresh?:boolean) {
  return (dispatch:Dispatch, getState:() => State) => {
    if (isRefresh) dispatch(setUserSettingsLoadedAction(false));
    const { userSettings: { userSettingsLoading, userSettingsLoaded } } = getState();

    if (userSettingsLoading || userSettingsLoaded) return;

    dispatch(setUserSettingsLoadingAction(true));

    return gql(`
      userSettings {
        searchColumns
        savedPlayersColumns
        teamPlayersColumns
        comparePlayersColumns
        comparePlayersResultType
        manageUsersColumns
        manageProgramsColumns
        isBoardLastViewOnDashboard
        cbtnUsername
        collegeDashboardSelectedYear
        nflDashboardSelectedYear
        profileStatsCategories
        profileStatsCategoriesNFL
        playerEvaluationValidationIsAll
      }
    `)
      .then((data:any) => data.userSettings as UserSettings)
      .then((userSettings:UserSettings) => {
        dispatch(setSearchColumnsAction(userSettings?.searchColumns || null));
        dispatch(setSavedPlayersColumnsAction(userSettings?.savedPlayersColumns || null));
        dispatch(setTeamPlayersColumnsAction(userSettings?.teamPlayersColumns || null));
        dispatch(setComparePlayersColumnsAction(userSettings?.comparePlayersColumns || null));
        dispatch(setComparePlayersResultTypeAction(userSettings?.comparePlayersResultType as CompareResultType || null));
        dispatch(setManageUsersColumnsAction(userSettings?.manageUsersColumns || null));
        dispatch(setManageProgramsColumnsAction(userSettings?.manageProgramsColumns || null));
        dispatch(setIsBoardLastViewOnDashboardAction(userSettings?.isBoardLastViewOnDashboard || false));
        dispatch(setUserSettingCtbnUsername(userSettings?.cbtnUsername || null));
        dispatch(setCollegeDashboardSelectedYearAction(userSettings?.collegeDashboardSelectedYear || null));
        dispatch(setNflDashboardSelectedYearAction(userSettings?.nflDashboardSelectedYear || null));
        dispatch(setProfileStatsCategoriesAction(userSettings?.profileStatsCategories || null));
        dispatch(setProfileStatsCategoriesNFLAction(userSettings?.profileStatsCategoriesNFL || null));
        dispatch(setPlayerEvaluationValidationIsAllAction(Boolean(userSettings?.playerEvaluationValidationIsAll)));
      })
      .catch(console.error)
      .finally(() => {
        dispatch(setUserSettingsLoadingAction(false));
        dispatch(setUserSettingsLoadedAction(true));
      });
  };
}

export function saveSearchColumns (searchColumns:Column[]) {
  return (dispatch:Dispatch, getState:() => State) => {
    const { userSettings: {
      savedPlayersColumns,
      teamPlayersColumns,
      comparePlayersColumns,
      comparePlayersResultType,
      manageUsersColumns,
      manageProgramsColumns,
    }} = getState();

    dispatch(setSearchColumnsAction(searchColumns));

    return saveOrUpdateUserSettings({
      searchColumns,
      teamPlayersColumns,
      savedPlayersColumns,
      comparePlayersColumns,
      comparePlayersResultType,
      manageUsersColumns,
      manageProgramsColumns,
    });
  };
}

export function saveTeamPlayersColumns (teamPlayersColumns:Column[]) {
  return (dispatch:Dispatch, getState:() => State) => {
    const { userSettings: {
      searchColumns,
      savedPlayersColumns,
      comparePlayersColumns,
      comparePlayersResultType,
      manageUsersColumns,
      manageProgramsColumns,
    }} = getState();

    dispatch(setTeamPlayersColumnsAction(teamPlayersColumns));

    return saveOrUpdateUserSettings({
      searchColumns,
      teamPlayersColumns,
      savedPlayersColumns,
      comparePlayersColumns,
      comparePlayersResultType,
      manageUsersColumns,
      manageProgramsColumns,
    });
  };
}

export function saveSavedPlayersColumns (savedPlayersColumns:Column[]) {
  return (dispatch:Dispatch, getState:() => State) => {
    const { userSettings: {
      searchColumns,
      teamPlayersColumns,
      comparePlayersColumns,
      comparePlayersResultType,
      manageUsersColumns,
      manageProgramsColumns,
    }} = getState();

    dispatch(setSavedPlayersColumnsAction(savedPlayersColumns));

    return saveOrUpdateUserSettings({
      searchColumns,
      teamPlayersColumns,
      savedPlayersColumns,
      comparePlayersColumns,
      comparePlayersResultType,
      manageUsersColumns,
      manageProgramsColumns,
    });
  };
}

export function saveComparePlayersColumnsAndType (comparePlayersColumns:Column[], resultType?:CompareResultType) {
  return (dispatch:Dispatch, getState:() => State) => {
    const { userSettings: {
      searchColumns,
      savedPlayersColumns,
      comparePlayersResultType,
      teamPlayersColumns,
      manageUsersColumns,
      manageProgramsColumns,
    }} = getState();

    dispatch(setComparePlayersColumnsAction(comparePlayersColumns));

    if (resultType) {
      dispatch(setComparePlayersResultTypeAction(resultType));
    }

    return saveOrUpdateUserSettings({
      searchColumns,
      teamPlayersColumns,
      savedPlayersColumns,
      comparePlayersColumns,
      comparePlayersResultType: resultType || comparePlayersResultType,
      manageUsersColumns,
      manageProgramsColumns,
    });
  };
}

export function saveManageUsersColumns (manageUsersColumns:Column[]) {
  return (dispatch:Dispatch, getState:() => State) => {
    const { userSettings: {
      searchColumns,
      savedPlayersColumns,
      teamPlayersColumns,
      comparePlayersColumns,
      comparePlayersResultType,
      manageProgramsColumns,
    }} = getState();

    dispatch(setManageUsersColumnsAction(manageUsersColumns));

    return saveOrUpdateUserSettings({
      searchColumns,
      teamPlayersColumns,
      savedPlayersColumns,
      comparePlayersColumns,
      comparePlayersResultType,
      manageUsersColumns,
      manageProgramsColumns,
    });
  };
}

export function saveManageProgramsColumns (manageProgramsColumns:Column[]) {
  return (dispatch:Dispatch, getState:() => State) => {
    const { userSettings: {
      searchColumns,
      savedPlayersColumns,
      teamPlayersColumns,
      comparePlayersColumns,
      comparePlayersResultType,
      manageUsersColumns,
    }} = getState();

    dispatch(setManageProgramsColumnsAction(manageProgramsColumns));

    return saveOrUpdateUserSettings({
      searchColumns,
      teamPlayersColumns,
      savedPlayersColumns,
      comparePlayersColumns,
      comparePlayersResultType,
      manageUsersColumns,
      manageProgramsColumns,
    });
  };
}

function saveOrUpdateUserSettings (userSettings:UserSettings) {
  return gql(`
    mutation {
      createOrUpdateUserSettings(
        userSettings: ${serializeGQLObject(userSettings)}
      )
    }
  `)
    .then((data:any) => data && data.createOrUpdateUserSettings as boolean)
    .catch(console.error);
}

export function updateUserBoardSettings (isBoardLastViewOnDashboard:boolean) {
  return (dispatch:Dispatch) => {
    dispatch(setIsBoardLastViewOnDashboardAction(isBoardLastViewOnDashboard));
    return gql(`
      mutation {
        updateUserBoardSettings(
          isBoardLastViewOnDashboard: ${isBoardLastViewOnDashboard}
        )
      }
    `)
      .then((data:any) => data && data.updateUserBoardSettings as boolean)
      .then(() => dispatch(fetchUserSettings(true) as any))
      .catch(console.error);
  };
}

export function updateUserCollegeDashboardSelectedYear (collegeDashboardSelectedYear:number) {
  return (dispatch:Dispatch, getState:() => State) => {
    const { userSettings: {
      searchColumns,
      savedPlayersColumns,
      teamPlayersColumns,
      comparePlayersColumns,
      comparePlayersResultType,
      manageUsersColumns,
      manageProgramsColumns,
      nflDashboardSelectedYear,
      profileStatsCategories,
      profileStatsCategoriesNFL,
      playerEvaluationValidationIsAll,
    }} = getState();
    dispatch(setCollegeDashboardSelectedYearAction(collegeDashboardSelectedYear));

    return saveOrUpdateUserSettings({
      searchColumns,
      teamPlayersColumns,
      savedPlayersColumns,
      comparePlayersColumns,
      comparePlayersResultType,
      manageUsersColumns,
      manageProgramsColumns,
      nflDashboardSelectedYear,
      collegeDashboardSelectedYear,
      profileStatsCategories,
      profileStatsCategoriesNFL,
      playerEvaluationValidationIsAll,
    });
  };
}

export function updateUserNflDashboardSelectedYear (nflDashboardSelectedYear:number) {
  return (dispatch:Dispatch, getState:() => State) => {
    const { userSettings: {
      searchColumns,
      savedPlayersColumns,
      teamPlayersColumns,
      comparePlayersColumns,
      comparePlayersResultType,
      manageUsersColumns,
      manageProgramsColumns,
      collegeDashboardSelectedYear,
      profileStatsCategories,
      profileStatsCategoriesNFL,
      playerEvaluationValidationIsAll,
    }} = getState();
    dispatch(setNflDashboardSelectedYearAction(nflDashboardSelectedYear));

    return saveOrUpdateUserSettings({
      searchColumns,
      teamPlayersColumns,
      savedPlayersColumns,
      comparePlayersColumns,
      comparePlayersResultType,
      manageUsersColumns,
      manageProgramsColumns,
      collegeDashboardSelectedYear,
      nflDashboardSelectedYear,
      profileStatsCategories,
      profileStatsCategoriesNFL,
      playerEvaluationValidationIsAll,
    });
  };
}

export function updateUserProfileStatsCategories (profileStatsCategories:string[]) {
  return (dispatch:Dispatch, getState:() => State) => {
    const { userSettings: {
      searchColumns,
      savedPlayersColumns,
      teamPlayersColumns,
      comparePlayersColumns,
      comparePlayersResultType,
      manageUsersColumns,
      manageProgramsColumns,
      collegeDashboardSelectedYear,
      nflDashboardSelectedYear,
      profileStatsCategoriesNFL,
      playerEvaluationValidationIsAll,
    }} = getState();

    return saveOrUpdateUserSettings({
      searchColumns,
      teamPlayersColumns,
      savedPlayersColumns,
      comparePlayersColumns,
      comparePlayersResultType,
      manageUsersColumns,
      manageProgramsColumns,
      collegeDashboardSelectedYear,
      nflDashboardSelectedYear,
      profileStatsCategories,
      profileStatsCategoriesNFL,
      playerEvaluationValidationIsAll,
    })
      .then((result:any) => {
        dispatch(fetchUserSettings(true) as any);
        return result;
      });
  };
}

export function updateUserProfileStatsCategoriesNFL (profileStatsCategoriesNFL:string[]) {
  return (dispatch:Dispatch, getState:() => State) => {
    const { userSettings: {
      searchColumns,
      savedPlayersColumns,
      teamPlayersColumns,
      comparePlayersColumns,
      comparePlayersResultType,
      manageUsersColumns,
      manageProgramsColumns,
      collegeDashboardSelectedYear,
      nflDashboardSelectedYear,
      profileStatsCategories,
      playerEvaluationValidationIsAll,
    }} = getState();

    return saveOrUpdateUserSettings({
      searchColumns,
      teamPlayersColumns,
      savedPlayersColumns,
      comparePlayersColumns,
      comparePlayersResultType,
      manageUsersColumns,
      manageProgramsColumns,
      collegeDashboardSelectedYear,
      nflDashboardSelectedYear,
      profileStatsCategories,
      profileStatsCategoriesNFL,
      playerEvaluationValidationIsAll,
    })
      .then((result:any) => {
        dispatch(fetchUserSettings(true) as any);
        return result;
      });
  };
}

export function updateUserPlayerEvaluationValidationIsAll (playerEvaluationValidationIsAll:boolean) {
  return (dispatch:Dispatch, getState:() => State) => {
    const { userSettings: {
      searchColumns,
      savedPlayersColumns,
      teamPlayersColumns,
      comparePlayersColumns,
      comparePlayersResultType,
      manageUsersColumns,
      manageProgramsColumns,
      collegeDashboardSelectedYear,
      nflDashboardSelectedYear,
      profileStatsCategories,
      profileStatsCategoriesNFL,
    }} = getState();

    dispatch(setPlayerEvaluationValidationIsAllAction(playerEvaluationValidationIsAll));

    return saveOrUpdateUserSettings({
      searchColumns,
      teamPlayersColumns,
      savedPlayersColumns,
      comparePlayersColumns,
      comparePlayersResultType,
      manageUsersColumns,
      manageProgramsColumns,
      collegeDashboardSelectedYear,
      nflDashboardSelectedYear,
      profileStatsCategories,
      profileStatsCategoriesNFL,
      playerEvaluationValidationIsAll,
    });
  };
}

export function updateUserPlayerValidationSetting (playerId:number, isChecked:boolean) {
  return (dispatch:Dispatch) => {
    return gql(`
      mutation {
        updateUserPlayerValidationSetting(
          playerId: ${playerId}
          isChecked: ${isChecked}
        )
      }
    `)
      .then((data:any) => data && data.updateUserPlayerValidationSetting as boolean)
      .then((isUpdated:boolean | undefined) => {
        if (isUpdated) {
          dispatch(setPlayerValidationSettingAction(playerId, isChecked));
        }

        return isUpdated;
      })
      .catch(console.error);
  };
}

export function fetchUserPlayerValidationSetting (playerId:number) {
  return (dispatch:Dispatch) => {
    return gql(`
      userPlayerValidationSetting(playerId: ${playerId})
    `)
      .then((data:any) => data?.userPlayerValidationSetting as boolean | undefined)
      .then((isChecked:boolean | undefined) => {
        if (typeof isChecked === 'boolean') {
          dispatch(setPlayerValidationSettingAction(playerId, isChecked));
        }

        return isChecked;
      })
      .catch(console.error);
  };
}
